import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams, useSearchParams } from 'react-router-dom';
import { useBrandColors } from 'src/UIKit';
import { useContactCaptureRecordController } from '../../controllers';
import {
  buildDefaultContactCaptureName,
  createContactCaptureCustomField,
  createBrandedContactCaptureSettings,
  getContactCaptureInstallSnippet,
  getContactCapturePublishErrors,
  normalizeContactCaptureSettings,
} from '../../helpers';
import {
  ContactCaptureCustomFieldSettings,
  ContactCaptureFieldOption,
  ContactCaptureFieldSettings,
  ContactCaptureSettings,
  ContactCaptureType,
} from '../../types';
import { useContactCaptureCustomFieldOptions } from './useContactCaptureCustomFieldOptions';
import { useContactCaptureCampaigns } from './useContactCaptureCampaigns';
import { useContactCapturePersistence } from './useContactCapturePersistence';
import { getContactCaptureDomainError } from '../../helpers/contactCaptureOrigins';

export type BuilderTab = 'content' | 'design' | 'install';

export const useContactCaptureBuilder = () => {
  const { id } = useParams<{ id: string }>();
  const controller = useContactCaptureRecordController(id);
  const [type, setType] = useState<ContactCaptureType>('popup');
  const [name, setName] = useState(() => (id ? '' : buildDefaultContactCaptureName()));
  const [domain, setDomain] = useState('');
  const brandColors = useBrandColors();
  const [settings, setSettings] = useState<ContactCaptureSettings>(
    () => createBrandedContactCaptureSettings(brandColors.primary),
  );
  const [tab, setTab] = useState<BuilderTab>('content');
  const [showValidation, setShowValidation] = useState(false);
  // A new capture opened from a freshly activated join campaign starts with that campaign.
  const [searchParams] = useSearchParams();
  const campaigns = useContactCaptureCampaigns(
    controller.companyId,
    controller.record?.campaignId,
    controller.record?.id,
    id ? null : searchParams.get('campaignId'),
  );
  const customFields = useContactCaptureCustomFieldOptions(controller.companyId);

  useEffect(() => {
    if (!controller.record) return;
    setType(controller.record.type);
    setName(controller.record.name);
    setDomain(controller.record.allowedOrigins?.[0] || '');
    setSettings(normalizeContactCaptureSettings(controller.record.settings));
  }, [controller.record]);

  const domainError = getContactCaptureDomainError(domain);
  const validationErrors = useMemo(() => [
    ...(!name.trim() ? [{ field: 'Name', message: 'Enter an internal name.' }] : []),
    ...(domainError ? [{ field: 'Website domain', message: domainError }] : []),
    ...getContactCapturePublishErrors(settings, campaigns.campaign),
  ], [domainError, campaigns.campaign, name, settings]);

  const getValidationError = useCallback(
    (field: string) => (showValidation
      ? validationErrors.find(error => error.field === field)
      : undefined),
    [showValidation, validationErrors],
  );

  const validate = useCallback(() => {
    setShowValidation(true);
    if (!validationErrors.length) return true;
    setTab('content');
    toast.error('Complete all required fields before saving.');
    return false;
  }, [validationErrors]);

  const updateContent = useCallback(
    <K extends keyof ContactCaptureSettings['content']>(key: K, value: ContactCaptureSettings['content'][K]) => {
      setSettings(current => ({ ...current, content: { ...current.content, [key]: value } }));
    },
    [],
  );

  const updateField = useCallback((
    fieldId: ContactCaptureFieldSettings['id'],
    patch: Partial<ContactCaptureFieldSettings>,
  ) => {
    setSettings(current => ({
      ...current,
      fields: current.fields.map(field => {
        if (field.id !== fieldId) return field;
        if (fieldId === 'mobilePhone') return { ...field, ...patch, enabled: true, required: true };
        return { ...field, ...patch };
      }),
    }));
  }, []);

  const addField = useCallback((field: ContactCaptureFieldOption) => {
    if (field.kind === 'platform') {
      updateField(field.id, { enabled: true });
      return;
    }
    const source = customFields.customFieldOptions.find(option => option.name === field.name);
    if (!source) return;
    setSettings(current => (current.customFields.some(item => item.name === field.name)
      ? current
      : { ...current, customFields: [...current.customFields, createContactCaptureCustomField(source)] }));
  }, [customFields.customFieldOptions, updateField]);

  const removeField = useCallback((field: ContactCaptureFieldOption) => {
    if (field.kind === 'platform') {
      updateField(field.id, { enabled: false, required: false });
      return;
    }
    setSettings(current => ({
      ...current,
      customFields: current.customFields.filter(item => item.name !== field.name),
    }));
  }, [updateField]);

  const updateCustomField = useCallback((
    name: string,
    patch: Partial<ContactCaptureCustomFieldSettings>,
  ) => {
    setSettings(current => ({
      ...current,
      customFields: current.customFields.map(field => (field.name === name ? { ...field, ...patch } : field)),
    }));
  }, []);

  const persistence = useContactCapturePersistence({
    controller,
    campaign: campaigns.campaign,
    id,
    name,
    domain,
    settings,
    type,
    validate,
  });

  return {
    id,
    controller,
    type,
    setType,
    name,
    setName,
    domain,
    setDomain,
    domainError: domain.trim() || showValidation ? domainError : null,
    settings,
    setSettings,
    tab,
    setTab,
    consentText: controller.record?.consentText || '',
    getValidationError,
    updateContent,
    updateField,
    addField,
    removeField,
    updateCustomField,
    ...customFields,
    ...campaigns,
    ...persistence,
    // Validation errors do not disable saving: clicking Save is what reveals them.
    formInvalid: campaigns.campaignsLoading || Boolean(campaigns.campaignsError),
    canInstall: Boolean(controller.record?.publicId),
    installValue: getContactCaptureInstallSnippet(type, controller.record?.publicId),
    formTypeLocked: Boolean(controller.record && controller.record.status !== 'draft'),
  };
};

export type ContactCaptureBuilder = ReturnType<typeof useContactCaptureBuilder>;
